import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '@/modules/auth/ui/AuthProvider';
import { useToast } from '@/hooks/useToast';
import DarkPageContainer from '@/shared/components/DarkPageContainer';
import { Card } from '@/shared/components/Card';
import { PrimaryButton, SecondaryButton } from '@/shared/components/Button';
import { DarkPageHeader, DarkAlert } from '@/shared/components/DarkFormElements';
import { CURRENCIES, getCurrencySymbol } from '../../../shared/constants/billing.js';
import CurrencySelectionModal from '@/shared/components/CurrencySelectionModal';
import { useUserPreferences, useUpdatePreferences, useOpenCustomerPortal } from '@/hooks/queries';
import { captureError } from '@/utils/errorHandler';

export default function AccountSettings() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast, toasts, hideToast } = useToast();
  
  const fromPath = location.state?.from || '/dashboard';
  
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);

  // Query hooks
  const { data: prefsData, isLoading: loading } = useUserPreferences();
  const updatePreferences = useUpdatePreferences();
  const openPortal = useOpenCustomerPortal();
  
  const preferredCurrency = prefsData?.preferredCurrency ?? null;
  const canChangeCurrency = !preferredCurrency;

  const handleCurrencyConfirm = async (newCurrency) => {
    try {
      await updatePreferences.mutateAsync({ preferredCurrency: newCurrency });
      setShowCurrencyModal(false);
      showToast('✅ Currency preference updated successfully!', 'success');
    } catch (error) {
      captureError(error, {
        action: 'handleCurrencyConfirm',
        component: 'AccountSettings',
        extra: { newCurrency },
      });
      showToast(error.message || 'Failed to update currency preference', 'error');
    }
  };

  const saving = updatePreferences.isPending;

  const handleOpenCustomerPortal = async () => {
    try {
      const returnUrl = `${window.location.origin}/account`;
      const data = await openPortal.mutateAsync(returnUrl);
      window.location.href = data.portalUrl;
    } catch (error) {
      captureError(error, {
        action: 'handleOpenCustomerPortal',
        component: 'AccountSettings',
      });
      showToast(error.message || 'Failed to open billing portal', 'error');
    }
  };

  return (
    <DarkPageContainer toasts={toasts} onCloseToast={hideToast} maxWidth="4xl">
      {/* Header */}
      <DarkPageHeader
        title="Account Settings"
        backLink={fromPath}
        backText="← Back to Apps"
      />

      {/* Account Information */}
      <Card variant="dark" className="mb-6">
        <h2 className="text-lg font-bold text-white mb-6">Account Information</h2>
        
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="w-full border border-white/10 rounded-xl px-4 py-3 bg-white/5 text-gray-400 cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 mt-1">
              This is your account email address
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Preferred Currency
            </label>
            {loading ? (
              <div className="text-sm text-gray-500">Loading...</div>
            ) : (
              <>
                {!preferredCurrency ? (
                  <PrimaryButton
                    onClick={() => setShowCurrencyModal(true)}
                    disabled={saving}
                    className="w-full"
                    size="md"
                  >
                    Select Preferred Currency
                  </PrimaryButton>
                ) : (
                  <>
                    <div className="w-full border border-white/10 rounded-xl px-4 py-3 bg-white/5 text-white">
                      <span className="font-medium">
                        {preferredCurrency.toUpperCase()} - {CURRENCIES.find(c => c.code === preferredCurrency)?.name || preferredCurrency.toUpperCase()} ({getCurrencySymbol(preferredCurrency)})
                      </span>
                    </div>
                    <DarkAlert variant="warning" icon="🔒" title="Currency Locked" className="mt-3">
                      <p className="mb-1">
                        Your preferred currency (<strong>{preferredCurrency.toUpperCase()}</strong>) is permanently locked and cannot be changed.
                      </p>
                      <p className="text-xs opacity-75">
                        This ensures consistency across all transactions, quotes, deposits, and subscriptions.
                      </p>
                    </DarkAlert>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </Card>

      {/* Billing & Invoices */}
      <Card variant="dark">
        <h2 className="text-lg font-bold text-white mb-6">Billing & Invoices</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-medium text-gray-300 mb-2">Payment History & Invoices</h3>
            <p className="text-sm text-gray-400 mb-4">
              View all your invoices, payment history, and manage your payment methods in the Stripe Customer Portal.
            </p>
            <PrimaryButton
              onClick={handleOpenCustomerPortal}
              size="md"
            >
              Open Billing Portal →
            </PrimaryButton>
          </div>
          
          <div className="pt-4 border-t border-white/10">
            <p className="text-xs text-gray-500 mb-2">
              The billing portal allows you to:
            </p>
            <ul className="text-xs text-gray-500 list-disc list-inside space-y-1">
              <li>View all invoices (monthly fees, build charges, etc.)</li>
              <li>Download PDF invoices</li>
              <li>Manage payment methods</li>
              <li>Update billing information</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Currency Selection Modal */}
      <CurrencySelectionModal
        isOpen={showCurrencyModal}
        onClose={() => setShowCurrencyModal(false)}
        onConfirm={handleCurrencyConfirm}
        initialCurrency="usd"
        requireConfirmation={true}
        context="account"
        isLoading={saving}
      />
    </DarkPageContainer>
  );
}
